import { useState, useRef, useCallback } from "react";
import { api, API } from "../lib/api";
import { Mic, MicOff, Loader2, Volume2 } from "lucide-react";

/**
 * Voice controller: records mic -> Whisper STT -> JARVIS chat -> TTS playback.
 * Manages orb state via onStateChange callback.
 */
export default function VoiceController({ onStateChange, onTranscript, onReply, orbState }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);

  const stopRec = useCallback(async () => {
    if (!mediaRef.current) return;
    return new Promise((resolve) => {
      mediaRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        chunksRef.current = [];
        mediaRef.current.stream.getTracks().forEach((t) => t.stop());
        resolve(blob);
      };
      mediaRef.current.stop();
    });
  }, []);

  const handleToggle = async () => {
    if (busy) return;
    if (recording) {
      setRecording(false);
      setBusy(true);
      onStateChange?.("thinking");
      try {
        const blob = await stopRec();
        // STT
        const fd = new FormData();
        fd.append("audio", blob, "voice.webm");
        const sttRes = await api.post("/jarvis/stt", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const text = sttRes.data.text?.trim();
        if (!text) {
          onStateChange?.("idle");
          setBusy(false);
          return;
        }
        onTranscript?.(text);
        await sendText(text);
      } catch (e) {
        console.error(e);
        onStateChange?.("idle");
      } finally {
        setBusy(false);
      }
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream);
        mediaRef.current = mr;
        chunksRef.current = [];
        mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
        mr.start();
        setRecording(true);
        onStateChange?.("listening");
      } catch (e) {
        alert("Mikrofon izni gerekli: " + e.message);
      }
    }
  };

  const sendText = async (text) => {
    onStateChange?.("thinking");
    try {
      const chatRes = await api.post("/jarvis/chat", {
        message: text,
        session_id: sessionRef.current,
      });
      sessionRef.current = chatRes.data.session_id;
      const reply = chatRes.data.reply;
      onReply?.(reply);

      // Play TTS
      onStateChange?.("speaking");
      const ttsRes = await fetch(`${API}/jarvis/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jarvis_token")}`,
        },
        body: JSON.stringify({ text: reply }),
      });
      if (ttsRes.ok) {
        const audioBlob = await ttsRes.blob();
        const url = URL.createObjectURL(audioBlob);
        if (audioRef.current) audioRef.current.pause();
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          onStateChange?.("idle");
          URL.revokeObjectURL(url);
        };
        audio.onerror = () => onStateChange?.("idle");
        await audio.play();
      } else {
        onStateChange?.("idle");
      }
    } catch (e) {
      console.error("JARVIS error:", e);
      onReply?.("Üzgünüm efendim, şu anda yanıt veremiyorum. Lütfen bir süre sonra tekrar deneyin.");
      onStateChange?.("idle");
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        data-testid="voice-mic-btn"
        onClick={handleToggle}
        disabled={busy}
        className={`relative h-14 w-14 rounded-full flex items-center justify-center transition-all ${
          recording
            ? "bg-rose-500/20 border-2 border-rose-400 animate-pulse"
            : "btn-cyan border-2"
        }`}
      >
        {busy ? (
          <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />
        ) : recording ? (
          <MicOff className="w-6 h-6 text-rose-300" />
        ) : (
          <Mic className="w-6 h-6" />
        )}
      </button>
      <div className="text-[10px] font-mono tracking-widest text-cyan-300/80 uppercase">
        {recording ? "Dinliyorum..." : busy ? "İşleniyor" : "Konuşmak için dokunun"}
      </div>
    </div>
  );
}

export function useJarvisSpeak() {
  const audioRef = useRef(null);
  return async (text, onStart, onEnd) => {
    try {
      onStart?.();
      const res = await fetch(`${API}/jarvis/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jarvis_token")}`,
        },
        body: JSON.stringify({ text }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { onEnd?.(); URL.revokeObjectURL(url); };
      a.onerror = () => onEnd?.();
      await a.play();
    } catch (e) {
      onEnd?.();
    }
  };
}
